/**
 * validation-rules.ts — OPEN-217 / DEC-382: a hauler's rules on what a driver types into a
 * ticket, and the ONE evaluator both the phone (offline, Hermes) and the API (the re-check at
 * completion, V8) run, so the two can never disagree about a value.
 *
 * A rule is set per job type FAMILY (it survives a version fork) and has a field from the
 * fixed catalogue below, a check, the message the driver sees, and a severity:
 *   WARNING — the driver confirms ("You entered 180 bbl. Are you sure?") and carries on; the
 *             confirmation, with a snapshot of the rule as shown, rides the payload and is kept
 *             on haul_captures.validation_confirmations for billing.
 *   BLOCK   — the driver cannot finish the step until the value passes.
 * The server never REJECTS a payload over a rule (offline-first: a rule must not strand a
 * completion); anything that got past the phone raises the VALIDATION_RULE review flag.
 *
 * ── What a check means ─────────────────────────────────────────────────────────
 * Each check names the condition that trips the rule:
 *   GT       value > min           ("more than 200")      — equal passes
 *   LT       value < max           ("less than 1")        — equal passes
 *   BETWEEN  value < min OR > max  ("must be between")    — the ends pass
 *   REQUIRED no value entered
 * A non-REQUIRED rule never fires on an empty value: emptiness is REQUIRED's job, or the job
 * type's own *_requirement setting.
 *
 * ── Exactness ──────────────────────────────────────────────────────────────────
 * Values arrive as numbers (the phone, Zod-parsed bodies) or decimal strings (Postgres
 * numeric). Every comparison is on integer THOUSANDTHS parsed from the decimal text, never
 * `x * 1000` on a float (1.005 * 1000 is 1004.999…). Thousandths because service and
 * line-item quantities are numeric(10,3); bbl (10,2), inches and minutes fit inside that.
 *
 * Pure and dependency-free apart from zod; no structuredClone / TextDecoder (Hermes).
 */

import { z } from 'zod';
import { formatRawLevel } from './tank-level-format.js';

// ─── Catalogue ──────────────────────────────────────────────────────────────────

/** Mirrors the job_type_validation_rules_field_check CHECK (migration 0266); change both. */
export const VALIDATION_RULE_FIELD_CODES = [
  'TANK_WATER_BBL',
  'TANK_OIL_BBL',
  'TANK_TOP_GAUGE_IN',
  'TANK_BOTTOM_GAUGE_IN',
  'TANK_GAUGE_CHANGE_IN',
  'TANK_BEFORE_BBL',
  'TANK_AFTER_BBL',
  'TANK_NET_BBL',
  'TICKET_NET_BBL',
  'TANK_COUNT',
  'LINE_ITEM_QTY',
  'SERVICE_QTY',
  'DRIVER_TIME_MIN',
] as const;
export type ValidationRuleField = (typeof VALIDATION_RULE_FIELD_CODES)[number];

export const VALIDATION_RULE_CHECK_KINDS = ['GT', 'LT', 'BETWEEN', 'REQUIRED'] as const;
export type ValidationRuleCheckKind = (typeof VALIDATION_RULE_CHECK_KINDS)[number];

export const VALIDATION_RULE_SEVERITIES = ['WARNING', 'BLOCK'] as const;
export type ValidationRuleSeverity = (typeof VALIDATION_RULE_SEVERITIES)[number];

/**
 * Where a rule is checked.
 *   TANK     — each tank (or line item) as it is saved on the run ticket.
 *   CAPTURE  — the whole ticket at "Done" (and an ON_SITE job's completion).
 *   DISPOSAL — the disposal leg's Complete.
 * SERVICE_QTY has no fixed stage: it follows its service charge's own screen (GAUGING → the
 * run ticket, CAPTURE; DISPOSAL → the disposal leg).
 */
export const VALIDATION_RULE_STAGES = ['TANK', 'CAPTURE', 'DISPOSAL'] as const;
export type ValidationRuleStage = (typeof VALIDATION_RULE_STAGES)[number];

export type ValidationRuleUnit = 'BBL' | 'IN' | 'MIN' | 'COUNT' | 'QTY';

export type ValidationCaptureMethod = 'SINGLE_GAUGE' | 'BEFORE_AFTER' | 'LINE_ITEM';
export type ValidationCompletionLocation = 'DISPOSAL_FACILITY' | 'ON_SITE' | 'OTHER';
export type ValidationFieldRequirement = 'HIDDEN' | 'OPTIONAL' | 'REQUIRED';

/** The job type settings that decide which fields a driver can enter at all. */
export interface ValidationRequirements {
  readonly water?: ValidationFieldRequirement | null;
  readonly oil?: ValidationFieldRequirement | null;
  readonly topGauge?: ValidationFieldRequirement | null;
  readonly bottomGauge?: ValidationFieldRequirement | null;
  readonly driverTime?: ValidationFieldRequirement | null;
}
type RequirementKey = keyof ValidationRequirements;

/**
 * The shape a ticket was captured under. Use the CAPTURE's own capture method and completion
 * location (both snapshotted on haul_captures; capture_method is edited in place without a
 * version fork), never the job type's live row.
 */
export interface ValidationShape {
  readonly captureMethod: ValidationCaptureMethod;
  readonly completionLocation: ValidationCompletionLocation;
  readonly requirements?: ValidationRequirements;
}

export interface ValidationFieldDef {
  readonly code: ValidationRuleField;
  readonly label: string;
  readonly unit: ValidationRuleUnit;
  /** NULL for SERVICE_QTY, whose stage comes from its service charge. */
  readonly stage: ValidationRuleStage | null;
  readonly captureMethods: readonly ValidationCaptureMethod[];
  readonly completionLocations?: readonly ValidationCompletionLocation[];
  /** The field is not entered at all when any of these is HIDDEN. */
  readonly requirementKeys?: readonly RequirementKey[];
  /**
   * Whether REQUIRED can be offered. Never for gauges (they default to 0 on the phone, so
   * "untouched" cannot be told from 0) or derived values. For a field with a requirement
   * setting it is offered only while that setting is OPTIONAL.
   */
  readonly requiredOffered: boolean;
}

const TANK_METHODS = ['SINGLE_GAUGE', 'BEFORE_AFTER'] as const;
const ALL_METHODS = ['SINGLE_GAUGE', 'BEFORE_AFTER', 'LINE_ITEM'] as const;

export const VALIDATION_RULE_FIELDS: Readonly<Record<ValidationRuleField, ValidationFieldDef>> = {
  TANK_WATER_BBL: {
    code: 'TANK_WATER_BBL',
    label: 'Tank water (bbl)',
    unit: 'BBL',
    stage: 'TANK',
    captureMethods: ['SINGLE_GAUGE'],
    requirementKeys: ['water'],
    requiredOffered: true,
  },
  TANK_OIL_BBL: {
    code: 'TANK_OIL_BBL',
    label: 'Tank oil (bbl)',
    unit: 'BBL',
    stage: 'TANK',
    captureMethods: ['SINGLE_GAUGE'],
    requirementKeys: ['oil'],
    requiredOffered: true,
  },
  TANK_TOP_GAUGE_IN: {
    code: 'TANK_TOP_GAUGE_IN',
    label: 'Top gauge',
    unit: 'IN',
    stage: 'TANK',
    captureMethods: ['SINGLE_GAUGE'],
    requirementKeys: ['topGauge'],
    requiredOffered: false,
  },
  TANK_BOTTOM_GAUGE_IN: {
    code: 'TANK_BOTTOM_GAUGE_IN',
    label: 'Bottom gauge',
    unit: 'IN',
    stage: 'TANK',
    captureMethods: ['SINGLE_GAUGE'],
    requirementKeys: ['bottomGauge'],
    requiredOffered: false,
  },
  TANK_GAUGE_CHANGE_IN: {
    code: 'TANK_GAUGE_CHANGE_IN',
    label: 'Gauge change (top − bottom)',
    unit: 'IN',
    stage: 'TANK',
    captureMethods: ['SINGLE_GAUGE'],
    requirementKeys: ['topGauge', 'bottomGauge'],
    requiredOffered: false,
  },
  TANK_BEFORE_BBL: {
    code: 'TANK_BEFORE_BBL',
    label: 'Tank before (bbl)',
    unit: 'BBL',
    stage: 'TANK',
    captureMethods: ['BEFORE_AFTER'],
    requiredOffered: false,
  },
  TANK_AFTER_BBL: {
    code: 'TANK_AFTER_BBL',
    label: 'Tank after (bbl)',
    unit: 'BBL',
    stage: 'TANK',
    captureMethods: ['BEFORE_AFTER'],
    requiredOffered: false,
  },
  TANK_NET_BBL: {
    code: 'TANK_NET_BBL',
    label: 'Tank net (after − before, bbl)',
    unit: 'BBL',
    stage: 'TANK',
    captureMethods: ['BEFORE_AFTER'],
    requiredOffered: false,
  },
  TICKET_NET_BBL: {
    code: 'TICKET_NET_BBL',
    label: 'Ticket net (bbl)',
    unit: 'BBL',
    stage: 'CAPTURE',
    captureMethods: TANK_METHODS,
    requiredOffered: false,
  },
  TANK_COUNT: {
    code: 'TANK_COUNT',
    label: 'Number of tanks',
    unit: 'COUNT',
    stage: 'CAPTURE',
    captureMethods: TANK_METHODS,
    requiredOffered: false,
  },
  LINE_ITEM_QTY: {
    code: 'LINE_ITEM_QTY',
    label: 'Line item quantity',
    unit: 'QTY',
    stage: 'TANK',
    captureMethods: ['LINE_ITEM'],
    requiredOffered: false,
  },
  SERVICE_QTY: {
    code: 'SERVICE_QTY',
    label: 'Extra service quantity',
    unit: 'QTY',
    stage: null,
    captureMethods: ALL_METHODS,
    requiredOffered: true,
  },
  DRIVER_TIME_MIN: {
    code: 'DRIVER_TIME_MIN',
    label: 'Driver total time',
    unit: 'MIN',
    stage: 'DISPOSAL',
    captureMethods: ALL_METHODS,
    // Entered only on the disposal-facility screen (FlexibleDestination and ON_SITE never ask).
    completionLocations: ['DISPOSAL_FACILITY'],
    requirementKeys: ['driverTime'],
    requiredOffered: true,
  },
};

const asRequirement = (v: string | null | undefined): ValidationFieldRequirement | null =>
  v === 'HIDDEN' || v === 'OPTIONAL' || v === 'REQUIRED' ? v : null;

/**
 * The shape of a job type row (live version for the editor; the pinned version, with the
 * capture's own capture method and completion location, for the re-check). A LINE_ITEM ticket
 * shape is captured as the LINE_ITEM capture method whatever capture_method says.
 */
export function validationShapeFromJobType(jt: {
  readonly captureMethod: string;
  readonly ticketShape?: string | null;
  readonly completionLocation: string;
  readonly waterVolumeRequirement?: string | null;
  readonly oilVolumeRequirement?: string | null;
  readonly topGaugeRequirement?: string | null;
  readonly bottomGaugeRequirement?: string | null;
  readonly driverTimeRequirement?: string | null;
}): ValidationShape {
  const captureMethod: ValidationCaptureMethod =
    jt.ticketShape === 'LINE_ITEM' || jt.captureMethod === 'LINE_ITEM'
      ? 'LINE_ITEM'
      : jt.captureMethod === 'BEFORE_AFTER'
        ? 'BEFORE_AFTER'
        : 'SINGLE_GAUGE';
  const completionLocation: ValidationCompletionLocation =
    jt.completionLocation === 'ON_SITE' || jt.completionLocation === 'OTHER'
      ? jt.completionLocation
      : 'DISPOSAL_FACILITY';
  return {
    captureMethod,
    completionLocation,
    requirements: {
      water: asRequirement(jt.waterVolumeRequirement),
      oil: asRequirement(jt.oilVolumeRequirement),
      topGauge: asRequirement(jt.topGaugeRequirement),
      bottomGauge: asRequirement(jt.bottomGaugeRequirement),
      driverTime: asRequirement(jt.driverTimeRequirement),
    },
  };
}

/** Whether the driver can enter this field at all under this shape. */
export function validationFieldApplies(
  field: ValidationRuleField,
  shape: ValidationShape,
): boolean {
  const def = VALIDATION_RULE_FIELDS[field];
  if (!def.captureMethods.includes(shape.captureMethod)) return false;
  if (def.completionLocations && !def.completionLocations.includes(shape.completionLocation)) {
    return false;
  }
  for (const key of def.requirementKeys ?? []) {
    if (shape.requirements?.[key] === 'HIDDEN') return false;
  }
  return true;
}

/** Whether the editor offers REQUIRED for this field under this shape. */
export function validationRequiredOffered(
  field: ValidationRuleField,
  shape: ValidationShape,
): boolean {
  const def = VALIDATION_RULE_FIELDS[field];
  if (!def.requiredOffered || !validationFieldApplies(field, shape)) return false;
  // Unknown (an older payload with no requirement) counts as OPTIONAL.
  return (def.requirementKeys ?? []).every(
    (k) => (shape.requirements?.[k] ?? 'OPTIONAL') === 'OPTIONAL',
  );
}

/** The stage a SERVICE_QTY rule runs in, from its service charge's screen. */
export function serviceChargeRuleStage(
  serviceChargeStage: string | null | undefined,
): ValidationRuleStage | null {
  if (serviceChargeStage === 'GAUGING') return 'CAPTURE';
  if (serviceChargeStage === 'DISPOSAL') return 'DISPOSAL';
  return null;
}

// ─── Exact numbers ──────────────────────────────────────────────────────────────

export type ValidationNumber = number | string | null | undefined;

/** Bounds and values are compared at this scale (thousandths). */
export const VALIDATION_SCALE = 1000;

/**
 * A number or decimal string as integer thousandths, half-up on the 4th decimal. NULL for
 * empty or unparseable input. Never multiplies a float.
 */
export function toThousandths(value: ValidationNumber): number | null {
  if (value === null || value === undefined) return null;
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    // toFixed(4) gives a decimal string with no exponent for any value in range.
    text = value.toFixed(4);
  } else {
    text = value.trim();
    if (text === '') return null;
  }
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(text);
  if (m === null) return null;
  const whole = m[2] ?? '';
  const frac = m[3] ?? '';
  if (whole === '' && frac === '') return null;
  const padded = (frac + '0000').slice(0, 4);
  let scaled = Number(whole === '' ? '0' : whole) * VALIDATION_SCALE + Number(padded.slice(0, 3));
  if (Number(padded[3]) >= 5) scaled += 1;
  if (!Number.isSafeInteger(scaled)) return null;
  return m[1] === '-' && scaled !== 0 ? -scaled : scaled;
}

const fromThousandths = (t: number): number => t / VALIDATION_SCALE;

/** Feet + inches → thousandths of an inch; NULL when neither was entered. */
function gaugeThousandths(feet: ValidationNumber, inches: ValidationNumber): number | null {
  const f = toThousandths(feet);
  const i = toThousandths(inches);
  if (f === null && i === null) return null;
  return (f ?? 0) * 12 + (i ?? 0);
}

// ─── Rules and values ───────────────────────────────────────────────────────────

/** A rule as the evaluator needs it (the reference-data row, or the API's table row). */
export interface ValidationRule {
  readonly id: string;
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: ValidationNumber;
  readonly maxValue: ValidationNumber;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
  readonly serviceChargeId?: string | null;
  /** SERVICE_QTY only: the service charge's own stage (GAUGING / DISPOSAL). */
  readonly serviceChargeStage?: string | null;
  readonly updatedAt?: string | null;
}

export interface ValidationTankValues {
  readonly position: number;
  readonly waterBbl?: ValidationNumber;
  readonly oilBbl?: ValidationNumber;
  readonly beforeBbl?: ValidationNumber;
  readonly afterBbl?: ValidationNumber;
  readonly topGaugeFeet?: ValidationNumber;
  readonly topGaugeInches?: ValidationNumber;
  readonly bottomGaugeFeet?: ValidationNumber;
  readonly bottomGaugeInches?: ValidationNumber;
}

/**
 * What the driver entered, as the phone builds it from the wire body
 * (captureValuesFromPayload) and the API from the stored rows. Absent = not part of this
 * check (e.g. no tanks at the disposal stage).
 */
export interface ValidationValues {
  readonly tanks?: readonly ValidationTankValues[] | undefined;
  readonly lineItems?:
    | readonly { readonly position: number; readonly quantity: ValidationNumber }[]
    | undefined;
  /** Summed per service charge by the evaluator. */
  readonly services?:
    | readonly {
        readonly serviceChargeId: string;
        readonly quantity: ValidationNumber;
      }[]
    | undefined;
  readonly driverTimeMinutes?: ValidationNumber;
}

export interface ValidationViolation {
  readonly ruleId: string;
  readonly field: ValidationRuleField;
  readonly severity: ValidationRuleSeverity;
  readonly stage: ValidationRuleStage;
  /** The tank's (or line item's) position; NULL for a ticket-level value. */
  readonly position: number | null;
  readonly serviceChargeId: string | null;
  /** The value in the field's unit (bbl, inches, minutes, count, qty); NULL for REQUIRED. */
  readonly value: number | null;
  /** The rule's message with {value} filled in, as the driver sees it. */
  readonly message: string;
}

export interface EvaluateOptions {
  readonly shape: ValidationShape;
  readonly stages: readonly ValidationRuleStage[];
}

function trips(rule: ValidationRule, valueT: number | null): boolean {
  if (rule.checkKind === 'REQUIRED') return valueT === null;
  if (valueT === null) return false;
  const min = toThousandths(rule.minValue);
  const max = toThousandths(rule.maxValue);
  switch (rule.checkKind) {
    case 'GT':
      return min !== null && valueT > min;
    case 'LT':
      return max !== null && valueT < max;
    case 'BETWEEN':
      return (min !== null && valueT < min) || (max !== null && valueT > max);
  }
}

function ruleStage(rule: ValidationRule): ValidationRuleStage | null {
  const def = VALIDATION_RULE_FIELDS[rule.field];
  return def.stage ?? serviceChargeRuleStage(rule.serviceChargeStage);
}

function tankValueThousandths(field: ValidationRuleField, t: ValidationTankValues): number | null {
  switch (field) {
    case 'TANK_WATER_BBL':
      return toThousandths(t.waterBbl);
    case 'TANK_OIL_BBL':
      return toThousandths(t.oilBbl);
    case 'TANK_BEFORE_BBL':
      return toThousandths(t.beforeBbl);
    case 'TANK_AFTER_BBL':
      return toThousandths(t.afterBbl);
    case 'TANK_NET_BBL': {
      const b = toThousandths(t.beforeBbl);
      const a = toThousandths(t.afterBbl);
      return a === null || b === null ? null : a - b;
    }
    case 'TANK_TOP_GAUGE_IN':
      return gaugeThousandths(t.topGaugeFeet, t.topGaugeInches);
    case 'TANK_BOTTOM_GAUGE_IN':
      return gaugeThousandths(t.bottomGaugeFeet, t.bottomGaugeInches);
    case 'TANK_GAUGE_CHANGE_IN': {
      const top = gaugeThousandths(t.topGaugeFeet, t.topGaugeInches);
      const bottom = gaugeThousandths(t.bottomGaugeFeet, t.bottomGaugeInches);
      return top === null || bottom === null ? null : top - bottom;
    }
    default:
      return null;
  }
}

/**
 * Ticket net bbl, the same formula as the server's VOLUME_ANOMALY: BEFORE_AFTER sums
 * max(after − before, 0) per tank, SINGLE_GAUGE sums water. NULL with no tanks.
 */
function ticketNetThousandths(
  method: ValidationCaptureMethod,
  tanks: readonly ValidationTankValues[],
): number | null {
  if (tanks.length === 0) return null;
  let sum = 0;
  for (const t of tanks) {
    if (method === 'BEFORE_AFTER') {
      const d = (toThousandths(t.afterBbl) ?? 0) - (toThousandths(t.beforeBbl) ?? 0);
      sum += Math.max(d, 0);
    } else {
      sum += toThousandths(t.waterBbl) ?? 0;
    }
  }
  return sum;
}

/**
 * Run the rules against what the driver entered, for the given stages. Rules whose field
 * doesn't apply to the shape, or whose stage isn't asked for, are skipped. Returns every
 * violation, BLOCKs first.
 */
export function evaluateValidationRules(
  rules: readonly ValidationRule[],
  values: ValidationValues,
  options: EvaluateOptions,
): ValidationViolation[] {
  const out: ValidationViolation[] = [];
  const { shape, stages } = options;
  const push = (
    rule: ValidationRule,
    stage: ValidationRuleStage,
    valueT: number | null,
    position: number | null,
  ): void => {
    if (!trips(rule, valueT)) return;
    const value = valueT === null ? null : fromThousandths(valueT);
    out.push({
      ruleId: rule.id,
      field: rule.field,
      severity: rule.severity,
      stage,
      position,
      serviceChargeId: rule.serviceChargeId ?? null,
      value,
      message: formatValidationMessage(rule.message, rule.field, value),
    });
  };

  for (const rule of rules) {
    if (!validationFieldApplies(rule.field, shape)) continue;
    const stage = ruleStage(rule);
    if (stage === null || !stages.includes(stage)) continue;

    switch (rule.field) {
      case 'TICKET_NET_BBL':
        if (values.tanks)
          push(rule, stage, ticketNetThousandths(shape.captureMethod, values.tanks), null);
        break;
      case 'TANK_COUNT':
        if (values.tanks && values.tanks.length > 0) {
          push(rule, stage, values.tanks.length * VALIDATION_SCALE, null);
        }
        break;
      case 'LINE_ITEM_QTY':
        for (const li of values.lineItems ?? []) {
          push(rule, stage, toThousandths(li.quantity), li.position);
        }
        break;
      case 'SERVICE_QTY': {
        if (!values.services || !rule.serviceChargeId) break;
        let total: number | null = null;
        for (const s of values.services) {
          if (s.serviceChargeId !== rule.serviceChargeId) continue;
          const q = toThousandths(s.quantity);
          if (q !== null) total = (total ?? 0) + q;
        }
        push(rule, stage, total, null);
        break;
      }
      case 'DRIVER_TIME_MIN':
        if (values.driverTimeMinutes !== undefined) {
          push(rule, stage, toThousandths(values.driverTimeMinutes), null);
        }
        break;
      default:
        for (const t of values.tanks ?? []) {
          push(rule, stage, tankValueThousandths(rule.field, t), t.position);
        }
    }
  }
  return out.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'BLOCK' ? -1 : 1));
}

// ─── Display ────────────────────────────────────────────────────────────────────

/** Up to `maxDecimals` places, trailing zeros dropped. No Intl: the same text on every engine. */
function trimNumber(v: number, maxDecimals: number): string {
  const fixed = v.toFixed(maxDecimals);
  return maxDecimals === 0 ? fixed : fixed.replace(/\.?0+$/, '');
}

/** A value in its field's unit as the driver reads it: "180 bbl", "8 ft 4 in", "11:30". */
export function formatValidationValue(field: ValidationRuleField, value: number): string {
  switch (VALIDATION_RULE_FIELDS[field].unit) {
    case 'BBL':
      return `${trimNumber(value, 2)} bbl`;
    case 'IN':
      return value < 0 ? `−${formatRawLevel(-value)}` : formatRawLevel(value);
    case 'MIN': {
      const total = Math.round(value);
      const h = Math.floor(total / 60);
      const m = total - h * 60;
      return `${String(h)}:${String(m).padStart(2, '0')}`;
    }
    case 'COUNT':
      return trimNumber(value, 0);
    case 'QTY':
      return trimNumber(value, 3);
  }
}

/** The rule's message with every {value} replaced by the formatted value. */
export function formatValidationMessage(
  message: string,
  field: ValidationRuleField,
  value: number | null,
): string {
  const shown = value === null ? 'nothing' : formatValidationValue(field, value);
  return message.split('{value}').join(shown);
}

// ─── Confirmations ──────────────────────────────────────────────────────────────

/** A snapshot of a rule as the driver saw it, so a later edit can't change what was shown. */
export interface ValidationRuleSnapshot {
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
}

export interface ValidationConfirmation {
  readonly ruleId: string;
  readonly stage: ValidationRuleStage;
  readonly position: number | null;
  readonly serviceChargeId: string | null;
  readonly value: number | null;
  /** The message as shown, {value} filled in. */
  readonly shownMessage: string;
  readonly confirmedAt: string;
  readonly rule: ValidationRuleSnapshot;
}

/** The confirmation a driver's "Yes, it's right" records. */
export function confirmationFor(
  violation: ValidationViolation,
  rule: ValidationRule,
  confirmedAt: string,
): ValidationConfirmation {
  const num = (v: ValidationNumber): number | null => {
    const t = toThousandths(v);
    return t === null ? null : fromThousandths(t);
  };
  return {
    ruleId: violation.ruleId,
    stage: violation.stage,
    position: violation.position,
    serviceChargeId: violation.serviceChargeId,
    value: violation.value,
    shownMessage: violation.message,
    confirmedAt,
    rule: {
      field: rule.field,
      checkKind: rule.checkKind,
      minValue: num(rule.minValue),
      maxValue: num(rule.maxValue),
      message: rule.message,
      severity: rule.severity,
    },
  };
}

const confirmationKey = (c: {
  ruleId: string;
  stage: string;
  position: number | null;
  serviceChargeId: string | null;
}): string => `${c.ruleId}|${c.stage}|${String(c.position)}|${c.serviceChargeId ?? ''}`;

/**
 * Whether the driver confirmed THIS violation: the same rule, place (tank position, service)
 * and value. Rule bounds may have changed since (the rule id still matches); the value may
 * not: a value edited after the driver confirmed it is not confirmed. A BLOCK is never
 * satisfied by a confirmation.
 */
export function isViolationConfirmed(
  violation: ValidationViolation,
  confirmations: readonly ValidationConfirmation[] | null | undefined,
): boolean {
  if (violation.severity === 'BLOCK' || !confirmations) return false;
  const v = toThousandths(violation.value);
  return confirmations.some(
    (c) =>
      c.ruleId === violation.ruleId &&
      c.position === violation.position &&
      (c.serviceChargeId ?? null) === violation.serviceChargeId &&
      toThousandths(c.value) === v,
  );
}

/** Violations the driver still has to act on: every BLOCK, and each unconfirmed WARNING. */
export function unresolvedViolations(
  violations: readonly ValidationViolation[],
  confirmations: readonly ValidationConfirmation[] | null | undefined,
): ValidationViolation[] {
  return violations.filter((v) => !isViolationConfirmed(v, confirmations));
}

/** At most this many confirmations are kept per capture; more are dropped, never rejected. */
export const VALIDATION_CONFIRMATIONS_MAX = 200;
const MESSAGE_MAX = 300;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanNumber(v: unknown): number | null {
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  const t = toThousandths(v);
  return t === null ? null : fromThousandths(t);
}

function cleanText(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : Array.from(t).slice(0, MESSAGE_MAX).join('');
}

function oneOf<T extends string>(list: readonly T[], v: unknown): T | null {
  return typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : null;
}

/**
 * The payload's validationConfirmations, cleaned: anything malformed is dropped, text is
 * truncated, duplicates (rule + stage + place) keep the last. Never throws. A non-array gives
 * [] (the build evaluated rules but sent junk); the caller maps an ABSENT field to NULL.
 */
export function normalizeValidationConfirmations(input: unknown): ValidationConfirmation[] {
  if (!Array.isArray(input)) return [];
  const byKey = new Map<string, ValidationConfirmation>();
  for (const raw of input.slice(0, VALIDATION_CONFIRMATIONS_MAX * 2)) {
    if (raw === null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const ruleId = typeof r.ruleId === 'string' && UUID_RE.test(r.ruleId) ? r.ruleId : null;
    const stage = oneOf(VALIDATION_RULE_STAGES, r.stage);
    const position =
      r.position === null || r.position === undefined
        ? null
        : typeof r.position === 'number' && Number.isInteger(r.position) && r.position >= 0
          ? r.position
          : undefined;
    const serviceChargeId =
      r.serviceChargeId === null || r.serviceChargeId === undefined
        ? null
        : typeof r.serviceChargeId === 'string' && UUID_RE.test(r.serviceChargeId)
          ? r.serviceChargeId
          : undefined;
    const confirmedAt =
      typeof r.confirmedAt === 'string' && !Number.isNaN(Date.parse(r.confirmedAt))
        ? r.confirmedAt
        : null;
    const snap = (r.rule ?? null) as Record<string, unknown> | null;
    const field = snap ? oneOf(VALIDATION_RULE_FIELD_CODES, snap.field) : null;
    const checkKind = snap ? oneOf(VALIDATION_RULE_CHECK_KINDS, snap.checkKind) : null;
    const severity = snap ? oneOf(VALIDATION_RULE_SEVERITIES, snap.severity) : null;
    const ruleMessage = snap ? cleanText(snap.message) : null;
    const shownMessage = cleanText(r.shownMessage);
    if (
      ruleId === null ||
      stage === null ||
      position === undefined ||
      serviceChargeId === undefined ||
      confirmedAt === null ||
      snap === null ||
      field === null ||
      checkKind === null ||
      severity === null ||
      ruleMessage === null ||
      shownMessage === null
    ) {
      continue;
    }
    const c: ValidationConfirmation = {
      ruleId,
      stage,
      position,
      serviceChargeId,
      value: cleanNumber(r.value),
      shownMessage,
      confirmedAt,
      rule: {
        field,
        checkKind,
        minValue: cleanNumber(snap.minValue),
        maxValue: cleanNumber(snap.maxValue),
        message: ruleMessage,
        severity,
      },
    };
    const key = confirmationKey(c);
    byKey.delete(key);
    byKey.set(key, c);
  }
  return Array.from(byKey.values()).slice(-VALIDATION_CONFIRMATIONS_MAX);
}

/**
 * Stored confirmations merged with an incoming set: same key → the incoming one wins.
 * Used by the disposal-complete append, and on a capture re-write with `keepStages` =
 * ['DISPOSAL'] (the re-written capture replaces its own TANK/CAPTURE entries and keeps the
 * disposal leg's).
 */
export function mergeValidationConfirmations(
  stored: readonly ValidationConfirmation[] | null | undefined,
  incoming: readonly ValidationConfirmation[],
  keepStages?: readonly ValidationRuleStage[],
): ValidationConfirmation[] {
  const kept = (stored ?? []).filter((c) => !keepStages || keepStages.includes(c.stage));
  return normalizeValidationConfirmations([...kept, ...incoming]);
}

// ─── Building values ────────────────────────────────────────────────────────────

/**
 * Values from a capture wire body (the phone's, before it is queued; the server's parsed
 * body). Reads only the documented keys, so either side gets the same numbers.
 */
export function captureValuesFromPayload(body: {
  readonly tanks?: readonly Record<string, unknown>[] | null;
  readonly lineItems?: readonly Record<string, unknown>[] | null;
  readonly serviceCharges?: readonly Record<string, unknown>[] | null;
}): ValidationValues {
  const n = (v: unknown): ValidationNumber =>
    typeof v === 'number' || typeof v === 'string' ? v : null;
  const pos = (v: unknown, i: number): number =>
    typeof v === 'number' && Number.isInteger(v) ? v : i;
  return {
    tanks: body.tanks?.map((t, i) => ({
      position: pos(t.position, i),
      waterBbl: n(t.waterVolumeBbl),
      oilBbl: n(t.oilVolumeBbl),
      beforeBbl: n(t.beforeBbl),
      afterBbl: n(t.afterBbl),
      topGaugeFeet: n(t.topGaugeFeet),
      topGaugeInches: n(t.topGaugeInches),
      bottomGaugeFeet: n(t.bottomGaugeFeet),
      bottomGaugeInches: n(t.bottomGaugeInches),
    })),
    lineItems: body.lineItems?.map((li, i) => ({
      position: pos(li.position, i),
      quantity: n(li.quantity),
    })),
    services: body.serviceCharges
      ?.filter(
        (s): s is Record<string, unknown> & { serviceChargeId: string } =>
          typeof s.serviceChargeId === 'string',
      )
      .map((s) => ({ serviceChargeId: s.serviceChargeId, quantity: n(s.quantity) })),
  };
}

// ─── Rule input (the API and the editor) ────────────────────────────────────────

/** Bounds are numeric(12,3). */
export const VALIDATION_RULE_BOUND_MAX = 999_999_999;
export const VALIDATION_RULE_MESSAGE_MAX = 200;

const bound = z.number().finite().min(-VALIDATION_RULE_BOUND_MAX).max(VALIDATION_RULE_BOUND_MAX);

/** The editable fields of a rule; mirrors the 0266 CHECKs. */
export const validationRuleFieldsInput = z.object({
  field: z.enum(VALIDATION_RULE_FIELD_CODES),
  checkKind: z.enum(VALIDATION_RULE_CHECK_KINDS),
  minValue: bound.nullable().optional(),
  maxValue: bound.nullable().optional(),
  // Prose the driver reads, kept as typed (DEC-147 notes exception).
  message: z.string().trim().min(1).max(VALIDATION_RULE_MESSAGE_MAX),
  severity: z.enum(VALIDATION_RULE_SEVERITIES),
  serviceChargeId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type ValidationRuleFieldsInput = z.infer<typeof validationRuleFieldsInput>;

export type ValidationRuleShapeError =
  | 'VALIDATION_RULE_BOUNDS_INVALID'
  | 'VALIDATION_RULE_SERVICE_CHARGE_INVALID';

/**
 * The 0266 shape CHECKs, for a whole rule (a create, or a PATCH merged onto the stored row):
 * GT has min only, LT max only, BETWEEN both with min ≤ max, REQUIRED neither; a service
 * charge exactly when the field is SERVICE_QTY. NULL when the shape is fine.
 */
export function validationRuleShapeError(r: {
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue?: ValidationNumber;
  readonly maxValue?: ValidationNumber;
  readonly serviceChargeId?: string | null;
}): ValidationRuleShapeError | null {
  const min = toThousandths(r.minValue);
  const max = toThousandths(r.maxValue);
  const ok =
    (r.checkKind === 'GT' && min !== null && max === null) ||
    (r.checkKind === 'LT' && min === null && max !== null) ||
    (r.checkKind === 'BETWEEN' && min !== null && max !== null && min <= max) ||
    (r.checkKind === 'REQUIRED' && min === null && max === null);
  if (!ok) return 'VALIDATION_RULE_BOUNDS_INVALID';
  if ((r.field === 'SERVICE_QTY') !== Boolean(r.serviceChargeId)) {
    return 'VALIDATION_RULE_SERVICE_CHARGE_INVALID';
  }
  return null;
}

// ─── Starter rules (D-10: offered in the editor, never seeded) ──────────────────

export interface SuggestedValidationRule {
  readonly code: string;
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
}

/** Paelode's starting points (Corbin, 2026-10-30): all WARNINGs, editable once added. */
export const SUGGESTED_VALIDATION_RULES: readonly SuggestedValidationRule[] = [
  {
    code: 'TICKET_NET_OVER_200_BBL',
    field: 'TICKET_NET_BBL',
    checkKind: 'GT',
    minValue: 200,
    maxValue: null,
    message: 'You entered {value} on this ticket. Are you sure?',
    severity: 'WARNING',
  },
  {
    code: 'GAUGE_CHANGE_OVER_20_FT',
    field: 'TANK_GAUGE_CHANGE_IN',
    checkKind: 'GT',
    minValue: 240,
    maxValue: null,
    message: 'Gauge change of {value} on this tank. Are you sure?',
    severity: 'WARNING',
  },
  {
    code: 'DRIVER_TIME_OVER_16_H',
    field: 'DRIVER_TIME_MIN',
    checkKind: 'GT',
    minValue: 960,
    maxValue: null,
    message: 'You entered {value} of driver time. Are you sure?',
    severity: 'WARNING',
  },
  {
    code: 'TANK_COUNT_OVER_6',
    field: 'TANK_COUNT',
    checkKind: 'GT',
    minValue: 6,
    maxValue: null,
    message: '{value} tanks on one ticket. Are you sure?',
    severity: 'WARNING',
  },
];

/** The suggestions that make sense for this shape. */
export function suggestedValidationRulesFor(shape: ValidationShape): SuggestedValidationRule[] {
  return SUGGESTED_VALIDATION_RULES.filter((s) => validationFieldApplies(s.field, shape));
}
